import React from 'react'
import PropTypes from 'prop-types'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import CurrentPrice from './current-price'
import ListPrice from './list-price'

/**
 * @param priceData - price info extracted from a product
 * // If a product is a set,
 *      on PLP, Show From X where X is the lowest of its children
 *      on PDP, Show From X where X is the lowest of its children and
 *          the set children will have it own price as From X (cross) Y
 * // if a product is a master
 *      on PLP and PDP, show From X (cross) Y , the X and Y are
 *          current and list price of variant that has the lowest price (including promotionalPrice)
 * // if a standard/bundle
 *      show exact price on PLP and PDP as X (cross) Y
 * @param currency - currency
 * @param quantity - quantity to take into the account for price display
 * @param currentPriceProps - extra props to be passing to CurrentPrice component
 * @param listPriceProps - extra props to be passing to ListPrice component
 * @param labelForA11y - label to be used for a11y
 */
const DisplayPrice = ({
    labelForA11y,
    priceData,
    isPriceRange,
    currency,
    quantity = 1,
    currentPriceProps = {},
    listPriceProps = {},
    isFromAddToCartPopup = false
}) => {
    const {listPrice, currentPrice, isASet, isMaster, isOnSale, isRange, maxPrice} = priceData
    const renderCurrentPrice = (isRange) => (
        <CurrentPrice
            labelForA11y={labelForA11y}
            price={currentPrice * quantity}
            as="b"
            currency={currency}
            isRange={isRange}
            {...currentPriceProps}
            isFromAddToCartPopup={isFromAddToCartPopup}
        />
    )
    const renderListPrice = (isRange) =>
        listPrice && (
            <ListPrice
                labelForA11y={labelForA11y}
                currency={currency}
                price={listPrice * quantity}
                isRange={isRange}
                {...listPriceProps}
                isFromAddToCartPopup={isFromAddToCartPopup}
            />
        )

    const showListPrice = isOnSale && Math.round(listPrice * 100) > Math.round(currentPrice * 100)

    const renderPriceSet = (isRange) => (
        <>
            {showListPrice && renderListPrice(isRange)} {renderCurrentPrice(isRange)}
        </>
    )

    if (isMaster) {
        if (isPriceRange && currentPrice !== undefined && maxPrice !== undefined) {
            return (
                <Box>
                    <CurrentPrice
                        labelForA11y={labelForA11y}
                        price={currentPrice * quantity}
                        as="b"
                        currency={currency}
                        isRange={true}
                        {...listPriceProps}
                    />
                    <Box as="span" mx="2px">
                        -
                    </Box>
                    <CurrentPrice
                        labelForA11y={labelForA11y}
                        price={maxPrice * quantity}
                        as="b"
                        currency={currency}
                        isRange={true}
                        {...listPriceProps}
                    />
                </Box>
            )
        } else {
            return renderPriceSet(isRange)
        }
    }

    if (isASet) {
        return renderCurrentPrice(true)
    }

    return <Box>{renderPriceSet(false)}</Box>
}

DisplayPrice.propTypes = {
    priceData: PropTypes.shape({
        currentPrice: PropTypes.number.isRequired,
        isOnSale: PropTypes.bool.isRequired,
        listPrice: PropTypes.number,
        pricePerUnit: PropTypes.number,
        isASet: PropTypes.bool,
        isMaster: PropTypes.bool,
        isRange: PropTypes.bool,
        maxPrice: PropTypes.number,
        tieredPrice: PropTypes.number
    }),
    currency: PropTypes.string.isRequired,
    labelForA11y: PropTypes.string,
    currentPriceProps: PropTypes.object,
    listPriceProps: PropTypes.object,
    quantity: PropTypes.number,
    isPriceRange: PropTypes.bool,
    isFromAddToCartPopup: PropTypes.bool
}

export default DisplayPrice
